import { AbcAnalysisSegmentLabel, resolveSegmentLabel } from '../abc-analysis-segment-label';
import {
  FALLBACK_SEGMENT_COLOR,
  resolveSegmentColor,
} from './abc-analysis-segment-color.function';
import { AbcAnalysisSegmentAggregateRow } from '../types/abc-analysis-segment-aggregate-row';
import { AbcAnalysisSegmentSummaryRow } from '../types/abc-analysis-segment-summary-row';

interface GrandTotal {
  articleCount: number;
  salesQuantity: number;
  contributionMargin: number;
  net: number;
  inventoryQuantity: number;
  inventoryValue: number;
}

function percentOf(part: number, total: number): number {
  return total === 0 ? 0 : (part / total) * 100;
}

export function buildSegmentSummary(
  rows: readonly AbcAnalysisSegmentAggregateRow[]
): AbcAnalysisSegmentSummaryRow[] {
  const grandTotal = rows.reduce<GrandTotal>(
    (acc, row) => ({
      articleCount: acc.articleCount + row.articleCount,
      salesQuantity: acc.salesQuantity + row.salesQuantity,
      contributionMargin: acc.contributionMargin + row.contributionMargin,
      net: acc.net + row.net,
      inventoryQuantity: acc.inventoryQuantity + row.inventoryQuantity,
      inventoryValue: acc.inventoryValue + row.inventoryValue,
    }),
    {
      articleCount: 0,
      salesQuantity: 0,
      contributionMargin: 0,
      net: 0,
      inventoryQuantity: 0,
      inventoryValue: 0,
    }
  );

  const knownOrder = Object.keys(AbcAnalysisSegmentLabel);
  const sortedRows = [...rows].sort((a, b) => {
    const indexA = knownOrder.indexOf(a.segment);
    const indexB = knownOrder.indexOf(b.segment);
    if (indexA === -1 && indexB === -1) return a.segment.localeCompare(b.segment);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  const toRow = (row: AbcAnalysisSegmentAggregateRow): AbcAnalysisSegmentSummaryRow => ({
    segment: row.segment,
    label: resolveSegmentLabel(row.segment),
    color: resolveSegmentColor(row.segment, row.segmentColor),
    articleCount: row.articleCount,
    articleCountPercent: percentOf(row.articleCount, grandTotal.articleCount),
    salesQuantity: row.salesQuantity,
    contributionMargin: row.contributionMargin,
    contributionMarginPercent: percentOf(row.contributionMargin, grandTotal.contributionMargin),
    net: row.net,
    netPercent: percentOf(row.net, grandTotal.net),
    inventoryQuantity: row.inventoryQuantity,
    inventoryValue: row.inventoryValue,
    inventoryValuePercent: percentOf(row.inventoryValue, grandTotal.inventoryValue),
  });

  const summaryRows = sortedRows.map(toRow);

  if (rows.length > 0) {
    summaryRows.push({
      segment: 'TOTAL',
      label: 'Gesamt',
      color: FALLBACK_SEGMENT_COLOR,
      articleCount: grandTotal.articleCount,
      articleCountPercent: 100,
      salesQuantity: grandTotal.salesQuantity,
      contributionMargin: grandTotal.contributionMargin,
      contributionMarginPercent: 100,
      net: grandTotal.net,
      netPercent: 100,
      inventoryQuantity: grandTotal.inventoryQuantity,
      inventoryValue: grandTotal.inventoryValue,
      inventoryValuePercent: 100,
    });
  }

  return summaryRows;
}
